/**
 * LocalStorage cache for i18n draft overrides.
 *
 * Allows instant preview of text changes before they are published.
 * Cache expires after 15 minutes to avoid stale data.
 */

const CACHE_KEY = 'i18n-draft-overrides';
const CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes

interface CacheEntry {
  namespace: string;
  key: string;
  locale: string;
  value: unknown;
  timestamp: number;
}

interface CacheData {
  entries: CacheEntry[];
  version: number;
}

function reportCacheError(operation: string, error: unknown): void {
  const normalizedError = error instanceof Error ? error : new Error(String(error));
  console.error(`[i18n draft cache] ${operation} failed`, normalizedError);
  void captureCacheException(normalizedError, operation);
}

async function captureCacheException(error: Error, operation: string): Promise<void> {
  try {
    const sentry = await import('@sentry/react');
    sentry.captureException(error, {
      tags: { subsystem: 'i18n-draft-cache', operation },
    });
  } catch (monitoringError) {
    const normalizedMonitoringError =
      monitoringError instanceof Error ? monitoringError : new Error(String(monitoringError));
    console.error('[monitoring] Sentry konnte den i18n-Cache-Fehler nicht erfassen', normalizedMonitoringError);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseCacheEntry(value: unknown, index: number): CacheEntry {
  if (
    !isRecord(value) ||
    typeof value.namespace !== 'string' ||
    typeof value.key !== 'string' ||
    typeof value.locale !== 'string' ||
    typeof value.timestamp !== 'number' ||
    !Number.isFinite(value.timestamp)
  ) {
    throw new TypeError(`Cache entry ${index} has an invalid structure`);
  }

  return {
    namespace: value.namespace,
    key: value.key,
    locale: value.locale,
    value: value.value,
    timestamp: value.timestamp,
  };
}

function parseCacheData(value: unknown): CacheData {
  if (
    !isRecord(value) ||
    !Array.isArray(value.entries) ||
    typeof value.version !== 'number' ||
    !Number.isSafeInteger(value.version) ||
    value.version < 0
  ) {
    throw new TypeError('Cached draft data has an invalid structure');
  }

  return {
    entries: value.entries.map(parseCacheEntry),
    version: value.version,
  };
}

function getCache(): CacheData {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return { entries: [], version: 0 };
    const data = parseCacheData(JSON.parse(raw) as unknown);
    // Filter out expired entries
    const now = Date.now();
    data.entries = data.entries.filter((e) => now - e.timestamp < CACHE_TTL_MS);
    return data;
  } catch (error) {
    reportCacheError('read', error);
    return { entries: [], version: 0 };
  }
}

function saveCache(data: CacheData): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch (error) {
    reportCacheError('write', error);
  }
}

/**
 * Add or update a draft override in the cache.
 */
export function cacheDraftOverride(namespace: string, key: string, locale: string, value: unknown): void {
  const cache = getCache();

  // Remove existing entry for this key
  cache.entries = cache.entries.filter((e) => !(e.namespace === namespace && e.key === key && e.locale === locale));

  // Add new entry
  cache.entries.push({
    namespace,
    key,
    locale,
    value,
    timestamp: Date.now(),
  });

  cache.version++;
  saveCache(cache);
}

/**
 * Get all cached draft overrides for a locale.
 * Returns a nested object: { namespace: { key: value } }
 */
export function getCachedDraftOverrides(locale: string): Record<string, Record<string, unknown>> {
  const cache = getCache();
  const result: Record<string, Record<string, unknown>> = {};

  for (const entry of cache.entries) {
    if (entry.locale !== locale) continue;

    const namespaceResult = (result[entry.namespace] ??= {});

    // Support dot-notation keys (e.g., "navigation.characters")
    const parts = entry.key.split('.');
    let cursor: Record<string, unknown> = namespaceResult;

    for (const [i, part] of parts.entries()) {
      const isLast = i === parts.length - 1;

      if (isLast) {
        cursor[part] = entry.value;
      } else {
        const nestedValue = cursor[part];
        if (!isRecord(nestedValue)) {
          const nestedResult: Record<string, unknown> = {};
          cursor[part] = nestedResult;
          cursor = nestedResult;
        } else {
          cursor = nestedValue;
        }
      }
    }
  }

  return result;
}

/**
 * Clear all cached draft overrides.
 * Call this after publishing or discarding changes.
 */
export function clearDraftOverridesCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (error) {
    reportCacheError('clear', error);
  }
}

/**
 * Check if there are any cached draft overrides.
 */
export function hasCachedDraftOverrides(): boolean {
  const cache = getCache();
  return cache.entries.length > 0;
}

/**
 * Get the number of cached draft overrides.
 */
export function getCachedDraftCount(): number {
  const cache = getCache();
  return cache.entries.length;
}
